import { useState } from "react";
import { toast } from "sonner";
import { marketingApi } from "@/lib/marketing";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Field, TextInput, TextArea, copyToClipboard } from "./shared";
import QRPanel from "./QRPanel";
import PaidAdsPanel from "./PaidAdsPanel";
import { LinkPerformance } from "./PerformancePanel";

const ReadOnly = ({ label, value }) => (
  <div>
    <p className="font-body text-xs uppercase tracking-wide text-ink-soft">{label}</p>
    <p className="font-body text-sm text-ink" data-testid={`detail-readonly-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{value || "—"}</p>
  </div>
);

export default function LinkDetailPanel({ link, open, onOpenChange, onChanged, defaultTab }) {
  const [edit, setEdit] = useState({ name: link.metadata.name, notes: link.metadata.notes || "", destination_url: link.destination_url });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await marketingApi.updateLink(link.link_id, edit);
      toast.success("Saved");
      onChanged();
    } catch (e) {
      toast.error(e.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const toggleStatus = async () => {
    setBusy(true);
    try {
      if (link.metadata.status === "active") await marketingApi.deactivateLink(link.link_id);
      else await marketingApi.reactivateLink(link.link_id);
      toast.success(link.metadata.status === "active" ? "Deactivated" : "Reactivated");
      onChanged();
    } catch (e) {
      toast.error(e.message || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const duplicate = async () => {
    setBusy(true);
    try {
      const dup = await marketingApi.duplicateLink(link.link_id);
      toast.success(`Duplicated as new link ${dup.link_id}`);
      onChanged();
    } catch (e) {
      toast.error(e.message || "Duplicate failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-paper border border-ink/15 max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="link-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-ink">{link.metadata.name}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-wrap gap-3 py-2">
          <button type="button" onClick={() => copyToClipboard(link.short_url, "Short URL")} data-testid="detail-copy-link" className="font-body text-xs uppercase tracking-[0.14em] text-terracotta hover:underline">Copy Link</button>
          <a href={link.destination_url} target="_blank" rel="noreferrer" data-testid="detail-open-destination" className="font-body text-xs uppercase tracking-[0.14em] text-terracotta hover:underline">Open Destination</a>
          <button type="button" onClick={duplicate} disabled={busy} data-testid="detail-duplicate-button" className="font-body text-xs uppercase tracking-[0.14em] text-terracotta hover:underline">Duplicate</button>
          <button type="button" onClick={toggleStatus} disabled={busy} data-testid="detail-toggle-status-button" className="font-body text-xs uppercase tracking-[0.14em] text-terracotta hover:underline">
            {link.metadata.status === "active" ? "Deactivate" : "Reactivate"}
          </button>
          <Badge className={link.metadata.status === "active" ? "bg-mustard text-ink border-transparent" : ""} data-testid="detail-status-badge">{link.metadata.status}</Badge>
        </div>

        <Tabs defaultValue={defaultTab || "details"}>
          <TabsList className="bg-paper-deep">
            <TabsTrigger value="details" data-testid="detail-tab-details">Details</TabsTrigger>
            <TabsTrigger value="qr" data-testid="detail-tab-qr">QR Code</TabsTrigger>
            <TabsTrigger value="paid-ads" data-testid="detail-tab-paid-ads">Paid Ads</TabsTrigger>
            <TabsTrigger value="performance" data-testid="detail-tab-performance">Performance</TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="mt-4 space-y-5">
            <div className="space-y-3 border border-ink/10 p-4">
              <p className="font-body text-xs uppercase tracking-[0.14em] text-terracotta">Editable — safe metadata only</p>
              <Field label="Name"><TextInput value={edit.name} onChange={(e) => setEdit((s) => ({ ...s, name: e.target.value }))} data-testid="detail-edit-name" /></Field>
              <Field label="Destination URL"><TextInput value={edit.destination_url} onChange={(e) => setEdit((s) => ({ ...s, destination_url: e.target.value }))} data-testid="detail-edit-destination" /></Field>
              <Field label="Notes"><TextArea rows={2} value={edit.notes} onChange={(e) => setEdit((s) => ({ ...s, notes: e.target.value }))} data-testid="detail-edit-notes" /></Field>
              <button type="button" onClick={save} disabled={busy} data-testid="detail-save-button" className="bg-ink text-paper py-2 px-4 font-body text-xs uppercase tracking-[0.14em] hover:bg-terracotta transition-colors disabled:opacity-50">Save</button>
              {link.metadata.destination_url_history?.length > 0 && (
                <div data-testid="detail-destination-history">
                  <p className="font-body text-xs uppercase tracking-wide text-ink-soft">Destination URL history</p>
                  {link.metadata.destination_url_history.map((h, i) => (
                    <p key={i} className="font-body text-xs text-ink-soft/80 break-all">{h.changed_at}: {h.url}</p>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-3 border border-ink/10 p-4">
              <p className="font-body text-xs uppercase tracking-[0.14em] text-ink-soft">Attribution — immutable (duplicate to change)</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <ReadOnly label="Purpose" value={link.metadata.purpose} />
                <ReadOnly label="Channel" value={link.metadata.channel} />
                <ReadOnly label="Placement" value={link.metadata.placement} />
                <ReadOnly label="Campaign" value={link.metadata.campaign_key} />
                <ReadOnly label="Offer" value={link.metadata.offer_key} />
                <ReadOnly label="Content / Asset" value={link.metadata.content_label} />
                <ReadOnly label="content_id" value={link.content_id} />
                <ReadOnly label="Parent link" value={link.metadata.parent_link_id} />
              </div>
              <ReadOnly label="UTM" value={Object.entries(link.utm).filter(([, v]) => v).map(([k, v]) => `${k}=${v}`).join(", ")} />
              <ReadOnly label="link_id" value={link.link_id} />
              <ReadOnly label="Created" value={`${link.metadata.created_at} · ${link.metadata.created_by.type}`} />
            </div>
          </TabsContent>

          <TabsContent value="qr" className="mt-4"><QRPanel link={link} /></TabsContent>
          <TabsContent value="paid-ads" className="mt-4"><PaidAdsPanel link={link} /></TabsContent>
          <TabsContent value="performance" className="mt-4"><LinkPerformance linkId={link.link_id} /></TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
